/* Outpolar product: variant selection, colour-linked media, native guides and Dawn cart drawer. */
(() => {
  if (customElements.get('op-product')) return;

  class OutpolarProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.data = JSON.parse(this.querySelector('[data-variant-data]').textContent);
      this.options = [...this.data.options];
      this.form = this.querySelector('form');
      this.mainButton = this.querySelector('[data-main-add]');
      this.sticky = this.querySelector('[data-sticky-add]');
      this.stickyButton = this.querySelector('[data-sticky-button]');
      this.error = this.querySelector('[data-product-error]');
      this.slides = this.querySelector('[data-slides]');
      this.slideItems = [...this.querySelectorAll('[data-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.knownColorAlts = new Set(this.data.variants.map((variant) => variant.imageAlt).filter(Boolean));
      this.pending = false;
      this.listener = new AbortController();
      const signal = this.listener.signal;
      this.querySelectorAll('[data-option-input]').forEach((input) => {
        input.addEventListener('change', () => {
          this.options[Number(input.closest('[data-option-index]').dataset.optionIndex)] = input.value;
          this.updateVariant(true);
        }, { signal });
      });
      this.form.addEventListener('submit', (event) => this.addToCart(event), { signal });
      this.stickyButton.addEventListener('click', () => this.form.requestSubmit(this.mainButton), { signal });
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goToMedia(thumb.dataset.mediaId), { signal }));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.moveGallery(-1), { signal });
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.moveGallery(1), { signal });
      this.slides.addEventListener('scroll', () => this.updateGalleryNavigation(), { passive: true, signal });
      this.slides.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.moveGallery(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, { signal });
      this.querySelectorAll('[data-open-dialog]').forEach((button) => {
        button.addEventListener('click', () => this.openGuide(button), { signal });
      });
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.querySelector('[data-close-dialog]')?.addEventListener('click', () => dialog.close(), { signal });
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        }, { signal });
        dialog.addEventListener('close', () => this.closeGuide(), { signal });
      });
      this.updateVariant(false);
      this.buttonVisible = true;
      this.buttonObserver = new IntersectionObserver((entries) => {
        this.buttonVisible = entries[0].isIntersecting;
        this.syncSticky();
      }, { threshold: 0 });
      this.buttonObserver.observe(this.mainButton);
      this.originalBodyPadding = document.body.style.paddingBottom;
      this.stickyObserver = new ResizeObserver(() => this.reserveStickySpace());
      this.stickyObserver.observe(this.sticky);
      window.addEventListener('popstate', () => {
        const id = new URL(window.location.href).searchParams.get('variant');
        const variant = this.data.variants.find((item) => String(item.id) === id);
        if (!variant) return;
        this.options = [...variant.options];
        this.querySelectorAll('[data-option-input]').forEach((input) => {
          input.checked = input.value === this.options[Number(input.closest('[data-option-index]').dataset.optionIndex)];
        });
        this.updateVariant(false);
      }, { signal });
    }

    disconnectedCallback() {
      this.listener?.abort();
      this.buttonObserver?.disconnect();
      this.stickyObserver?.disconnect();
      if (this.originalBodyPadding !== undefined) document.body.style.paddingBottom = this.originalBodyPadding;
      if (this.guideOpen) this.closeGuide();
      this.initialized = false;
    }

    updateVariant(updateUrl) {
      this.variant = this.data.variants.find((variant) => variant.options.every((value, index) => value === this.options[index]));
      const variant = this.variant;
      const idInput = this.querySelector('[data-variant-id]');
      idInput.value = variant?.id || '';
      idInput.disabled = !variant;
      this.error.hidden = true;
      this.querySelectorAll('[data-option-index]').forEach((fieldset) => {
        const index = Number(fieldset.dataset.optionIndex);
        fieldset.querySelector('[data-option-value]').textContent = this.options[index];
        fieldset.querySelectorAll('[data-option-input]').forEach((input) => {
          const possible = this.data.variants.some((candidate) => candidate.available && candidate.options.every((value, optionIndex) => value === (optionIndex === index ? input.value : this.options[optionIndex])));
          input.closest('label').classList.toggle('is-unavailable', !possible);
          input.setAttribute('aria-label', `${input.value}${possible ? '' : ' — Épuisé dans cette combinaison'}`);
        });
      });
      const stock = this.querySelector('[data-stock]');
      stock.textContent = variant?.available ? 'En stock' : variant ? 'Épuisé' : 'Combinaison indisponible';
      stock.classList.toggle('is-sold-out', !variant?.available);
      this.querySelectorAll('[data-price]').forEach((element) => { element.textContent = variant?.price || '—'; });
      const compare = this.querySelector('[data-compare-price]');
      compare.hidden = !variant?.onSale;
      compare.textContent = variant?.comparePrice || '';
      this.querySelector('[data-sticky-variant]').textContent = variant && variant.title !== 'Default Title' ? variant.title : '';
      this.querySelectorAll('[data-selected-vlt]').forEach((element) => { element.textContent = variant?.vlt || 'Valeur non renseignée pour ce choix'; });
      this.querySelectorAll('[data-vlt-variant]').forEach((element) => { element.textContent = variant?.title || this.options.join(' / '); });
      this.setButtonState();
      if (variant) {
        this.filterMedia(variant);
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }
      this.dispatchEvent(new CustomEvent('outpolar:variant-change', { bubbles: true, detail: { variant } }));
    }

    setButtonState() {
      const available = Boolean(this.variant?.available);
      this.querySelectorAll('[data-option-index]').forEach((fieldset) => { fieldset.disabled = this.pending; });
      [this.mainButton, this.stickyButton].forEach((button) => {
        button.disabled = this.pending || !available;
        button.setAttribute('aria-busy', String(this.pending));
        button.querySelector('[data-add-label]').textContent = this.pending ? 'Ajout en cours…' : available ? 'Ajouter au panier' : this.variant ? 'Épuisé' : 'Indisponible';
      });
    }

    filterMedia(variant) {
      const hasColor = Number(this.dataset.colorIndex) >= 0;
      const selectedAlt = variant.imageAlt;
      const selectedId = String(variant.mediaId);
      const showMedia = (media) => {
        const alt = media.dataset.mediaAlt;
        // An alt shared by all views of a colour is the explicit group key. Unlinked images remain common.
        return !hasColor || !selectedAlt || alt === selectedAlt || !this.knownColorAlts.has(alt);
      };
      this.slideItems.forEach((slide) => { slide.hidden = !showMedia(slide); });
      this.thumbs.forEach((thumb) => { thumb.hidden = !showMedia(thumb); });
      this.visibleSlides = this.slideItems.filter((slide) => !slide.hidden);
      this.goToMedia(this.visibleSlides.some((slide) => slide.dataset.mediaId === selectedId) ? selectedId : this.visibleSlides[0]?.dataset.mediaId, false);
    }

    goToMedia(mediaId, smooth = true) {
      const index = this.visibleSlides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) return;
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.slides.scrollTo({ left: index * this.slides.clientWidth, behavior: smooth && !reduced ? 'smooth' : 'instant' });
      this.updateGalleryNavigation(index);
    }

    moveGallery(direction) {
      const current = Math.round(this.slides.scrollLeft / Math.max(this.slides.clientWidth, 1));
      const index = Math.min(Math.max(current + direction, 0), this.visibleSlides.length - 1);
      this.goToMedia(this.visibleSlides[index]?.dataset.mediaId);
    }

    updateGalleryNavigation(forcedIndex) {
      const visible = this.visibleSlides || this.slideItems;
      const index = typeof forcedIndex === 'number' ? forcedIndex : Math.round(this.slides.scrollLeft / Math.max(this.slides.clientWidth, 1));
      this.thumbs.forEach((thumb) => thumb.setAttribute('aria-pressed', String(thumb.dataset.mediaId === visible[index]?.dataset.mediaId)));
      this.querySelector('[data-gallery-count]').textContent = visible.length ? `${index + 1} / ${visible.length}` : '';
      this.querySelector('[data-gallery-prev]').disabled = index <= 0;
      this.querySelector('[data-gallery-next]').disabled = index >= visible.length - 1;
    }

    syncSticky() {
      this.sticky.hidden = this.buttonVisible || Boolean(this.guideOpen);
      this.reserveStickySpace();
    }

    reserveStickySpace() {
      if (this.originalBodyPadding === undefined) return;
      document.body.style.paddingBottom = this.sticky.hidden ? this.originalBodyPadding : `${this.sticky.offsetHeight}px`;
    }

    openGuide(button) {
      const dialog = document.getElementById(button.dataset.openDialog);
      if (!dialog) return;
      this.guideTrigger = button;
      this.previousOverflow = document.documentElement.style.overflow;
      this.guideOpen = true;
      document.documentElement.style.overflow = 'hidden';
      dialog.showModal();
      this.syncSticky();
    }

    closeGuide() {
      this.guideOpen = false;
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.guideTrigger?.focus({ preventScroll: true });
      this.syncSticky();
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.pending || !this.variant?.available) return;
      this.pending = true;
      this.error.hidden = true;
      this.setButtonState();
      const drawer = document.querySelector('cart-drawer');
      const data = new FormData(this.form);
      data.set('id', String(this.variant.id));
      data.set('quantity', '1');
      if (drawer?.getSectionsToRender) {
        data.set('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
        data.set('sections_url', window.location.pathname);
        drawer.setActiveElement(document.activeElement);
      }
      try {
        const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data,
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          const message = typeof result.description === 'string' ? result.description : 'Ce produit ne peut pas être ajouté pour le moment. Vérifiez votre choix et réessayez.';
          throw new Error(message);
        }
        if (drawer?.renderContents && result.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'outpolar-product', productVariantId: this.variant.id, cartData: result });
          }
        } else {
          window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
        }
      } catch (error) {
        this.error.textContent = error instanceof TypeError ? 'La connexion a été interrompue. Vérifiez votre connexion puis réessayez.' : error.message;
        this.error.hidden = false;
        // Keep an error visible even when the buyer used the fixed button.
        this.error.tabIndex = -1;
        this.error.focus({ preventScroll: false });
      } finally {
        this.pending = false;
        this.setButtonState();
      }
    }
  }
  customElements.define('op-product', OutpolarProduct);
})();
